"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { Icon, Pill } from "@/components/Icons";
import { SearchableSelect } from "@/components/SearchableSelect";
import { useStock } from "@/context/StockContext";
import type { ProduitVendu } from "@/lib/pennylane/assoc-types";
import type { FusionGroupe } from "@/lib/pennylane/fusion-types";
import type { PackComposition } from "@/lib/pennylane/pack-types";

type Mode = "doublons" | "packs";
type Filtre = "a-fusionner" | "fusionnes";
type Tri = "alpha" | "alpha-desc" | "qte";
type Portee = "tous" | "libres";

const PAGE_SIZE = 50;

type DraftComposant = { produitId: string | null; qte: number };

export function FusionsView() {
  const { showToast } = useStock();
  const [loading, setLoading] = useState(false);
  const [ventes, setVentes] = useState<ProduitVendu[]>([]);
  const [fusionnes, setFusionnes] = useState<ProduitVendu[]>([]);
  const [groupes, setGroupes] = useState<FusionGroupe[]>([]);
  const [packs, setPacks] = useState<PackComposition[]>([]);
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [mode, setMode] = useState<Mode>("doublons");
  const [filtre, setFiltre] = useState<Filtre>("a-fusionner");
  const [tri, setTri] = useState<Tri>("alpha");
  const [portee, setPortee] = useState<Portee>("tous");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [toutAfficher, setToutAfficher] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);
  const [masterId, setMasterId] = useState<string | null>(null);

  const [editPackId, setEditPackId] = useState<string | null>(null);
  const [draftPackId, setDraftPackId] = useState<string | null>(null);
  const [draftComposants, setDraftComposants] = useState<DraftComposant[]>([
    { produitId: null, qte: 1 },
    { produitId: null, qte: 1 },
  ]);
  const [savingPack, setSavingPack] = useState(false);

  const byId = useMemo(() => new Map(ventes.map((v) => [v.id, v])), [ventes]);
  const groupeByMaster = useMemo(
    () => new Map(groupes.map((g) => [g.masterId, g])),
    [groupes],
  );
  const displayById = useMemo(() => new Map(fusionnes.map((v) => [v.id, v])), [fusionnes]);
  const packByProduit = useMemo(() => new Map(packs.map((p) => [p.packId, p])), [packs]);

  const sync = useCallback(async () => {
    setLoading(true);
    try {
      const [fusRes, packRes] = await Promise.all([
        fetch("/api/pennylane/fusions"),
        fetch("/api/pennylane/packs"),
      ]);
      const fusData = await fusRes.json();
      if (!fusRes.ok) {
        showToast(fusData.error || "Impossible de charger les produits");
        return;
      }
      setVentes(fusData.ventes ?? []);
      setFusionnes(fusData.fusionnes ?? fusData.ventes ?? []);
      setGroupes(fusData.groupes ?? []);
      setUpdatedAt(fusData.updatedAt ?? null);

      if (packRes.ok) {
        const packData = await packRes.json();
        setPacks(packData.packs ?? []);
        if (packData.updatedAt && (!fusData.updatedAt || packData.updatedAt > fusData.updatedAt)) {
          setUpdatedAt(packData.updatedAt);
        }
      }

      showToast(
        `${fusData.stats?.apresFusion ?? fusData.stats?.produits ?? 0} lignes · ${fusData.stats?.groupes ?? 0} fusion${(fusData.stats?.groupes ?? 0) > 1 ? "s" : ""}`,
      );
    } catch {
      showToast("Erreur réseau");
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    void sync();
  }, [sync]);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      if (!prev.includes(id) && groupeByMaster.has(id)) {
        setMasterId(id);
      } else if (masterId && !next.includes(masterId)) {
        setMasterId(next.find((x) => groupeByMaster.has(x)) ?? next[0] ?? null);
      } else if (!masterId && next.length) {
        setMasterId(next.find((x) => groupeByMaster.has(x)) ?? next[0]);
      }
      return next;
    });
  };

  const clearSelection = () => {
    setSelected([]);
    setMasterId(null);
  };

  const renameVente = async (id: string, texte: string, groupeId?: string | null) => {
    const res = await fetch("/api/pennylane/libelles", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ kind: "vente", id, texte, groupeId: groupeId ?? null }),
    });
    const data = await res.json();
    if (!res.ok) {
      showToast(data.error || "Renommage impossible");
      return;
    }
    if (data.ventes) setVentes(data.ventes);
    if (data.fusionnes) setFusionnes(data.fusionnes);
    if (data.groupes) setGroupes(data.groupes);
    showToast("Libellé enregistré");
  };

  const merge = async () => {
    if (selected.length < 2) {
      showToast("Coche au moins 2 libellés (ex. un maître + un oublié)");
      return;
    }
    const master =
      masterId && selected.includes(masterId)
        ? masterId
        : selected.find((id) => groupeByMaster.has(id)) ??
          selected.reduce((best, id) => {
            const a = displayById.get(id) ?? byId.get(id);
            const b = displayById.get(best) ?? byId.get(best);
            return (a?.qte ?? 0) >= (b?.qte ?? 0) ? id : best;
          }, selected[0]);

    const res = await fetch("/api/pennylane/fusions", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        action: "merge",
        memberIds: selected,
        masterId: master,
        label: displayById.get(master)?.texte ?? byId.get(master)?.texte ?? null,
      }),
    });
    const data = await res.json();
    if (!res.ok) {
      showToast(data.error || "Fusion impossible");
      return;
    }
    setVentes(data.ventes ?? ventes);
    setFusionnes(data.fusionnes ?? []);
    setGroupes(data.groupes ?? []);
    setUpdatedAt(data.updatedAt ?? null);
    clearSelection();
    showToast("Fusion enregistrée");
  };

  const unmerge = async (groupeId: string) => {
    const res = await fetch("/api/pennylane/fusions", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action: "unmerge", groupeId }),
    });
    const data = await res.json();
    if (!res.ok) {
      showToast(data.error || "Annulation impossible");
      return;
    }
    setFusionnes(data.fusionnes ?? []);
    setGroupes(data.groupes ?? []);
    setUpdatedAt(data.updatedAt ?? null);
    showToast("Fusion annulée");
  };

  const resetPackForm = () => {
    setEditPackId(null);
    setDraftPackId(null);
    setDraftComposants([
      { produitId: null, qte: 1 },
      { produitId: null, qte: 1 },
    ]);
  };

  const loadPackIntoForm = (p: PackComposition) => {
    setEditPackId(p.id);
    setDraftPackId(p.packId);
    setDraftComposants(
      p.composants.length
        ? p.composants.map((c) => ({ produitId: c.produitId, qte: c.qte }))
        : [{ produitId: null, qte: 1 }],
    );
  };

  const savePack = async () => {
    if (!draftPackId) {
      showToast("Choisis le produit pack");
      return;
    }
    const composants = draftComposants
      .filter((c) => c.produitId)
      .map((c) => ({ produitId: c.produitId!, qte: c.qte > 0 ? c.qte : 1 }));
    if (composants.length < 1) {
      showToast("Ajoute au moins 1 composant");
      return;
    }
    setSavingPack(true);
    try {
      const res = await fetch("/api/pennylane/packs", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "save",
          id: editPackId ?? undefined,
          packId: draftPackId,
          label: displayById.get(draftPackId)?.texte ?? null,
          composants,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        showToast(data.error || "Enregistrement impossible");
        return;
      }
      setPacks(data.packs ?? []);
      setUpdatedAt(data.updatedAt ?? null);
      resetPackForm();
      showToast("Pack enregistré");
    } catch {
      showToast("Erreur réseau");
    } finally {
      setSavingPack(false);
    }
  };

  const deletePack = async (id: string) => {
    const res = await fetch("/api/pennylane/packs", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action: "delete", id }),
    });
    const data = await res.json();
    if (!res.ok) {
      showToast(data.error || "Suppression impossible");
      return;
    }
    setPacks(data.packs ?? []);
    setUpdatedAt(data.updatedAt ?? null);
    if (editPackId === id) resetPackForm();
    showToast("Pack supprimé");
  };

  const qKey = q.trim().toLowerCase();
  const qTokens = useMemo(
    () =>
      qKey
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .split(/[^a-z0-9]+/i)
        .filter(Boolean),
    [qKey],
  );
  const matchQ = useCallback(
    (texte: string) => {
      if (!qTokens.length) return true;
      const hay = texte
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");
      return qTokens.every((t) => hay.includes(t));
    },
    [qTokens],
  );
  const cmpAlpha = (a: string, b: string) => a.localeCompare(b, "fr", { sensitivity: "base" });

  const candidats = useMemo(() => {
    const list = fusionnes.filter((v) => {
      if (!matchQ(v.texte)) return false;
      if (portee === "libres" && groupeByMaster.has(v.id)) return false;
      return true;
    });
    if (tri === "qte") return [...list].sort((a, b) => b.qte - a.qte || cmpAlpha(a.texte, b.texte));
    if (tri === "alpha-desc") return [...list].sort((a, b) => cmpAlpha(b.texte, a.texte));
    return [...list].sort((a, b) => cmpAlpha(a.texte, b.texte));
  }, [fusionnes, matchQ, tri, portee, groupeByMaster]);

  const totalPages = Math.max(1, Math.ceil(candidats.length / PAGE_SIZE));
  const pageSafe = Math.min(page, totalPages);
  const pageRows = useMemo(() => {
    if (toutAfficher) return candidats;
    const start = (pageSafe - 1) * PAGE_SIZE;
    return candidats.slice(start, start + PAGE_SIZE);
  }, [candidats, pageSafe, toutAfficher]);

  const listKey = `${mode}\0${q}\0${tri}\0${portee}\0${filtre}`;
  const [seenListKey, setSeenListKey] = useState(listKey);
  if (seenListKey !== listKey) {
    setSeenListKey(listKey);
    if (page !== 1) setPage(1);
  }

  const groupesFiltres = useMemo(() => {
    const list = groupes.filter((g) =>
      matchQ(g.label ?? byId.get(g.masterId)?.texte ?? displayById.get(g.masterId)?.texte ?? ""),
    );
    const labelOf = (g: FusionGroupe) =>
      g.label ?? displayById.get(g.masterId)?.texte ?? byId.get(g.masterId)?.texte ?? g.masterId;
    if (tri === "qte") {
      return [...list].sort((a, b) => {
        const qa = displayById.get(a.masterId)?.qte ?? 0;
        const qb = displayById.get(b.masterId)?.qte ?? 0;
        return qb - qa || cmpAlpha(labelOf(a), labelOf(b));
      });
    }
    if (tri === "alpha-desc") {
      return [...list].sort((a, b) => cmpAlpha(labelOf(b), labelOf(a)));
    }
    return [...list].sort((a, b) => cmpAlpha(labelOf(a), labelOf(b)));
  }, [groupes, byId, displayById, matchQ, tri]);

  const packsFiltres = useMemo(() => {
    const list = packs.filter((p) => {
      const label = p.label ?? displayById.get(p.packId)?.texte ?? p.packId;
      const comps = p.composants
        .map((c) => displayById.get(c.produitId)?.texte ?? "")
        .join(" ");
      return matchQ(`${label} ${comps}`);
    });
    const labelOf = (p: PackComposition) =>
      p.label ?? displayById.get(p.packId)?.texte ?? p.packId;
    if (tri === "alpha-desc") return [...list].sort((a, b) => cmpAlpha(labelOf(b), labelOf(a)));
    return [...list].sort((a, b) => cmpAlpha(labelOf(a), labelOf(b)));
  }, [packs, displayById, matchQ, tri]);

  /** Catalogue pour choisir le produit pack (exclure les packs déjà créés, sauf édition). */
  const packProduitOptions = useMemo(() => {
    return fusionnes
      .filter((v) => v.id === draftPackId || !packByProduit.has(v.id))
      .map((v) => ({
        id: v.id,
        label: v.texte,
        meta: `${v.qte} vendus`,
      }));
  }, [fusionnes, packByProduit, draftPackId]);

  /** Composants = unitaires seulement (pas de packs). */
  const composantOptions = useMemo(
    () =>
      fusionnes
        .filter((v) => v.id !== draftPackId && !packByProduit.has(v.id))
        .map((v) => ({
          id: v.id,
          label: v.texte,
          meta: `${v.qte} vendus`,
        })),
    [fusionnes, packByProduit, draftPackId],
  );

  const nbAlias = useMemo(
    () => groupes.reduce((n, g) => n + Math.max(0, g.memberIds.length - 1), 0),
    [groupes],
  );

  return (
    <>
      <p className="muted" style={{ maxWidth: "70ch", marginBottom: 14 }}>
        {mode === "doublons" ? (
          <>
            Cherche, coche les doublons, fusionne. Les <b>maîtres déjà fusionnés restent dans la liste</b>{" "}
            (badge Fusion) : tu peux en cocher un + un libellé oublié pour l’ajouter au groupe.
          </>
        ) : (
          <>
            Associe un <b>pack vendu</b> à ses composants. En associations / stock, 10 packs
            « moteur + variateur + hotte » ajoutent <b>+10 sur chaque unitaire</b> — le pack
            n’est plus proposé dans les listes (stock = unitaires).
          </>
        )}
      </p>

      <div className="assoc-meta">
        <span>
          Stockage :{" "}
          <b className="mono">
            {mode === "packs" ? "data/vente-packs.json" : "data/vente-fusions.json"}
          </b>
        </span>
        <span>
          Lignes : <b>{fusionnes.length}</b>
        </span>
        <span>
          Fusions : <b>{groupes.length}</b>
          {nbAlias ? ` · ${nbAlias} libellés rattachés` : ""}
        </span>
        <span>
          Packs : <b>{packs.length}</b>
        </span>
        {updatedAt && (
          <span>
            Dernier enreg. :{" "}
            <b>
              {new Date(updatedAt).toLocaleString("fr-FR", {
                day: "2-digit",
                month: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
              })}
            </b>
          </span>
        )}
      </div>

      <div className="toolbar" style={{ marginBottom: 10 }}>
        <div className="chips">
          {(
            [
              ["doublons", "Doublons"],
              ["packs", `Packs (${packs.length})`],
            ] as const
          ).map(([id, lib]) => (
            <button
              key={id}
              className="chip"
              type="button"
              aria-pressed={mode === id}
              onClick={() => {
                setMode(id);
                clearSelection();
              }}
            >
              {lib}
            </button>
          ))}
        </div>
        <div className="search">
          <Icon name="search" />
          <input
            type="search"
            placeholder={
              mode === "packs"
                ? "Ex. pack moteur variateur, hotte…"
                : "Ex. tourelle T32 230v, réduction 315/200…"
            }
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        {mode === "doublons" && (
          <>
            <div className="chips">
              {(
                [
                  ["a-fusionner", `Catalogue (${candidats.length})`],
                  ["fusionnes", `Détail fusions (${groupes.length})`],
                ] as const
              ).map(([id, lib]) => (
                <button
                  key={id}
                  className="chip"
                  type="button"
                  aria-pressed={filtre === id}
                  onClick={() => {
                    setFiltre(id);
                    clearSelection();
                  }}
                >
                  {lib}
                </button>
              ))}
            </div>
            <label className="muted" style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              Tri
              <select
                value={tri}
                onChange={(e) => setTri(e.target.value as Tri)}
                aria-label="Trier les libellés"
              >
                <option value="alpha">A → Z</option>
                <option value="alpha-desc">Z → A</option>
                <option value="qte">Qté vendue</option>
              </select>
            </label>
            <label className="muted" style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              Afficher
              <select
                value={portee}
                onChange={(e) => setPortee(e.target.value as Portee)}
                aria-label="Tout ou uniquement les libres"
              >
                <option value="tous">Tout</option>
                <option value="libres">Uniquement les libres</option>
              </select>
            </label>
          </>
        )}
        {mode === "packs" && (
          <label className="muted" style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            Tri
            <select
              value={tri === "qte" ? "alpha" : tri}
              onChange={(e) => setTri(e.target.value as Tri)}
              aria-label="Trier les packs"
            >
              <option value="alpha">A → Z</option>
              <option value="alpha-desc">Z → A</option>
            </select>
          </label>
        )}
        <button className="btn small" type="button" disabled={loading} onClick={() => void sync()}>
          <Icon name="sync" /> {loading ? "…" : "Actualiser"}
        </button>
      </div>

      {mode === "packs" && (
        <>
          <div className="pack-form">
            <div className="pack-form-row">
              <label>{editPackId ? "Modifier le pack" : "Nouveau pack"}</label>
              <SearchableSelect
                options={packProduitOptions}
                value={draftPackId}
                placeholder="Rechercher le pack vendu…"
                onChange={(id) => setDraftPackId(id)}
              />
            </div>
            <div className="pack-form-row">
              <label>Composants (produits inclus dans le pack)</label>
              <div className="pack-composants">
                {draftComposants.map((c, i) => (
                  <div key={i} className="pack-composant">
                    <SearchableSelect
                      options={composantOptions}
                      value={c.produitId}
                      placeholder="Ex. moteur, variateur, hotte…"
                      onChange={(id) =>
                        setDraftComposants((prev) =>
                          prev.map((row, j) => (j === i ? { ...row, produitId: id } : row)),
                        )
                      }
                    />
                    <input
                      type="number"
                      min={0.001}
                      step={1}
                      value={c.qte}
                      aria-label="Quantité"
                      onChange={(e) =>
                        setDraftComposants((prev) =>
                          prev.map((row, j) =>
                            j === i
                              ? { ...row, qte: Math.max(0.001, Number(e.target.value) || 1) }
                              : row,
                          ),
                        )
                      }
                    />
                    <button
                      className="btn small"
                      type="button"
                      disabled={draftComposants.length <= 1}
                      onClick={() =>
                        setDraftComposants((prev) => prev.filter((_, j) => j !== i))
                      }
                    >
                      Retirer
                    </button>
                  </div>
                ))}
              </div>
            </div>
            <div className="pack-form-acts">
              <button
                className="btn small"
                type="button"
                onClick={() =>
                  setDraftComposants((prev) => [...prev, { produitId: null, qte: 1 }])
                }
              >
                + Composant
              </button>
              {editPackId && (
                <button className="btn small" type="button" onClick={resetPackForm}>
                  Annuler
                </button>
              )}
              <button
                className="btn primary"
                type="button"
                disabled={savingPack}
                onClick={() => void savePack()}
              >
                {savingPack ? "…" : editPackId ? "Enregistrer" : "Créer le pack"}
              </button>
            </div>
          </div>

          <div className="fusion-list">
            {packsFiltres.length === 0 && !loading && (
              <p className="muted">Aucun pack pour l’instant — crée-en un ci-dessus.</p>
            )}
            {packsFiltres.map((p) => {
              const label =
                p.label ?? displayById.get(p.packId)?.texte ?? byId.get(p.packId)?.texte ?? p.packId;
              const packsVendus = displayById.get(p.packId)?.qte ?? byId.get(p.packId)?.qte ?? 0;
              return (
                <article key={p.id} className="fusion-card pack-card">
                  <header>
                    <div>
                      <Pill kind="ok">Pack</Pill>
                      <b style={{ marginLeft: 8 }}>{label}</b>
                      <span className="muted">
                        {" "}
                        · {packsVendus} vendus · {p.composants.length} composant
                        {p.composants.length > 1 ? "s" : ""}
                      </span>
                    </div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <button className="btn small" type="button" onClick={() => loadPackIntoForm(p)}>
                        Modifier
                      </button>
                      <button
                        className="btn small"
                        type="button"
                        onClick={() => void deletePack(p.id)}
                      >
                        Supprimer
                      </button>
                    </div>
                  </header>
                  <ul>
                    {p.composants.map((c) => {
                      const texte =
                        displayById.get(c.produitId)?.texte ??
                        byId.get(c.produitId)?.texte ??
                        c.produitId;
                      const ajoute = Math.round(packsVendus * c.qte * 1000) / 1000;
                      return (
                        <li key={c.produitId} className="composant">
                          <span>{texte}</span>
                          <span className="muted mono">
                            ×{c.qte}/pack
                            {packsVendus > 0 ? ` → +${ajoute} stock` : ""}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </article>
              );
            })}
          </div>
        </>
      )}

      {mode === "doublons" && filtre === "a-fusionner" && (
        <>
          <div className="fusion-banner">
            <span>
              {selected.length === 0
                ? "Coche un maître et/ou des libellés à regrouper"
                : selected.length === 1
                  ? "1 sélectionné — coche un autre doublon (ou un maître existant)"
                  : `${selected.length} sélectionnés`}
              {selected.length >= 2 && masterId ? (
                <>
                  {" "}
                  · maître :{" "}
                  <b>
                    {(displayById.get(masterId) ?? byId.get(masterId))?.texte?.slice(0, 70)}
                  </b>
                </>
              ) : null}
            </span>
          </div>

          {selected.length > 0 && (
            <div className="fusion-action-bar" role="region" aria-label="Actions de fusion">
              <span>
                <b>{selected.length}</b> sélectionné{selected.length > 1 ? "s" : ""}
                {selected.length >= 2 && masterId ? (
                  <>
                    {" "}
                    · maître :{" "}
                    <b>
                      {(displayById.get(masterId) ?? byId.get(masterId))?.texte?.slice(0, 50)}
                    </b>
                  </>
                ) : null}
              </span>
              <div className="fusion-action-bar-acts">
                <button className="btn small" type="button" onClick={clearSelection}>
                  Tout décocher
                </button>
                <button
                  className="btn primary"
                  type="button"
                  disabled={selected.length < 2}
                  onClick={() => void merge()}
                >
                  Fusionner la sélection
                </button>
              </div>
            </div>
          )}

          <div className="pager">
            <span className="muted">
              {candidats.length === 0
                ? "0 produit"
                : toutAfficher
                  ? `${candidats.length} produit${candidats.length > 1 ? "s" : ""} (tous)`
                  : `${(pageSafe - 1) * PAGE_SIZE + 1}–${Math.min(pageSafe * PAGE_SIZE, candidats.length)} sur ${candidats.length}`}
            </span>
            <div className="pager-acts">
              <label className="remember" style={{ marginTop: 0 }}>
                <input
                  type="checkbox"
                  checked={toutAfficher}
                  onChange={(e) => {
                    setToutAfficher(e.target.checked);
                    setPage(1);
                  }}
                />
                Tout afficher sur une page
              </label>
              {!toutAfficher && (
                <>
                  <button
                    className="btn small"
                    type="button"
                    disabled={pageSafe <= 1}
                    onClick={() => setPage(1)}
                  >
                    «
                  </button>
                  <button
                    className="btn small"
                    type="button"
                    disabled={pageSafe <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    Préc.
                  </button>
                  <label className="muted" style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    Page
                    <select
                      value={pageSafe}
                      onChange={(e) => setPage(Number(e.target.value))}
                      aria-label="Numéro de page"
                    >
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
                        <option key={n} value={n}>
                          {n} / {totalPages}
                        </option>
                      ))}
                    </select>
                  </label>
                  <button
                    className="btn small"
                    type="button"
                    disabled={pageSafe >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  >
                    Suiv.
                  </button>
                  <button
                    className="btn small"
                    type="button"
                    disabled={pageSafe >= totalPages}
                    onClick={() => setPage(totalPages)}
                  >
                    »
                  </button>
                </>
              )}
            </div>
          </div>

          <div className={`table-wrap${selected.length ? " has-action-bar" : ""}`}>
            <table>
              <thead>
                <tr>
                  <th style={{ width: 36 }} />
                  <th>Libellé</th>
                  <th>Qté</th>
                  <th>Statut</th>
                  <th>Maître</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((v) => {
                  const on = selected.includes(v.id);
                  const g = groupeByMaster.get(v.id);
                  const isPack = packByProduit.has(v.id);
                  return (
                    <tr key={v.id} className={on ? "row-selected" : undefined}>
                      <td>
                        <input
                          type="checkbox"
                          checked={on}
                          onChange={() => toggle(v.id)}
                          aria-label="Sélectionner"
                        />
                      </td>
                      <td>
                        <EditableLibelle
                          value={v.texte}
                          onSave={(texte) => renameVente(v.id, texte, g?.id)}
                        />
                        <div className="muted mono">
                          {v.qte} vendus · {v.factures} fact.
                          {g ? ` · ${g.memberIds.length} libellés liés` : ""}
                          {isPack ? " · pack" : ""}
                        </div>
                      </td>
                      <td className="mono">{v.qte}</td>
                      <td>
                        {g ? (
                          <Pill kind="ok">Fusion ×{g.memberIds.length}</Pill>
                        ) : isPack ? (
                          <Pill kind="ok">Pack</Pill>
                        ) : (
                          <Pill kind="warn">Libre</Pill>
                        )}
                      </td>
                      <td>
                        {on ? (
                          <button
                            type="button"
                            className={`btn small${masterId === v.id ? " primary" : ""}`}
                            onClick={() => setMasterId(v.id)}
                          >
                            {masterId === v.id ? "Maître" : "Choisir"}
                          </button>
                        ) : (
                          "—"
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {candidats.length === 0 && !loading && (
              <p className="muted" style={{ padding: 14 }}>
                {qTokens.length
                  ? `Aucun libellé contenant « ${qTokens.join(" » + « ")} ».`
                  : "Aucun produit à afficher."}
              </p>
            )}
          </div>
        </>
      )}

      {mode === "doublons" && filtre === "fusionnes" && (
        <div className="fusion-list">
          {groupesFiltres.length === 0 && !loading && (
            <p className="muted">Aucune fusion pour l’instant.</p>
          )}
          {groupesFiltres.map((g) => {
            const members = g.memberIds
              .map((id) => byId.get(id))
              .filter(Boolean) as ProduitVendu[];
            const label =
              g.label ?? displayById.get(g.masterId)?.texte ?? byId.get(g.masterId)?.texte ?? g.masterId;
            return (
              <article key={g.id} className="fusion-card">
                <header>
                  <div>
                    <Pill kind="ok">Fusion</Pill>
                    <EditableLibelle
                      className="fusion-card-title"
                      value={label}
                      onSave={(texte) => renameVente(g.masterId, texte, g.id)}
                    />
                    <span className="muted">
                      {" "}
                      · {g.memberIds.length} libellés ·{" "}
                      {displayById.get(g.masterId)?.qte ??
                        members.reduce((n, m) => n + m.qte, 0)}{" "}
                      vendus
                    </span>
                  </div>
                  <button className="btn small" type="button" onClick={() => void unmerge(g.id)}>
                    Séparer
                  </button>
                </header>
                <ul>
                  {members.map((m) => (
                    <li key={m.id} className={m.id === g.masterId ? "master" : undefined}>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 4, minWidth: 0 }}>
                        {m.id === g.masterId ? "★ " : ""}
                        <EditableLibelle
                          value={m.texte}
                          onSave={(texte) => renameVente(m.id, texte)}
                        />
                      </span>
                      <span className="muted mono">
                        {m.qte} · {m.factures} fact.
                      </span>
                    </li>
                  ))}
                </ul>
              </article>
            );
          })}
        </div>
      )}

      {loading && <p className="muted">Chargement…</p>}
    </>
  );
}
